/**
 * InfoTooltip — Phase 6a.12/F-1.
 *
 * Inline ⓘ icon-button that opens a shadcn Tooltip with IFRS guidance
 * for an adjacent form field. Used by the 3 always-open contract form
 * sections to surface terse audit-context hints (e.g., the difference
 * between IBR and the lessor's implicit rate, when to include extension
 * options in lease term, the annual vs periodic discount-rate
 * convention).
 *
 * Bilingual: `content` and `ariaLabel` are passed in by the caller so the
 * caller can resolve them via `t()` without this component depending on
 * the i18n key union.
 */

import { Info } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface InfoTooltipProps {
  content: string;
  ariaLabel: string;
  /** Test hook for component tests. */
  "data-testid"?: string;
  className?: string;
}

export function InfoTooltip({
  content,
  ariaLabel,
  className,
  ...rest
}: InfoTooltipProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          data-testid={rest["data-testid"]}
          className={cn(
            "inline-flex items-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm",
            className,
          )}
        >
          <Info className="h-4 w-4" aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" align="start" className="max-w-xs">
        <p className="text-xs leading-relaxed">{content}</p>
      </TooltipContent>
    </Tooltip>
  );
}
